/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { batch, effect } from '@preact/signals-react'

import { AUTH_MODALS_INFO } from '@/constants/auth'
import { decryptData, encryptData, reverseStringValue } from '@/helpers'
import offlineDB from '@/indexeddb'
import sessionsDB from '@/indexeddb/sessions'
import {
  type TForgotPwdData,
  type TResetPwdData,
  type TSignupData,
  type TSignIn,
  type TAuthUserPermissionResponse,
  EAuthModalTypes,
  type TAuthCurrentUserState,
} from '@/interfaces'
import {
  AltchaState,
  ApplicationsState,
  AuthState,
  ConvocatoriasState,
  OfflineState,
} from '@/state'

import {
  applicationsStateActions,
  convocatoriasStateActions,
  formStateActions,
} from '../actions'
import { setPermisionsToForm } from '../form/utils'
import {
  cleanDBOnLogOut,
  initSyncAfterLogin,
} from '../offline-management/utils'
import appRequests from '../requests'

import {
  getOfflineDataByUser,
  getOfflineUserId,
  setUserPermissions,
} from './utils'

export const authStateActions = {
  getInitialData: async () => {
    await Promise.all([
      convocatoriasStateActions.getUserConvocatorias(),
      formStateActions.getCurrentForm(),
      applicationsStateActions.getAllApplications(),
    ])
  },
  getMe: async () => {
    const { data: userData } = await appRequests.Auth.authMe()

    batch(() => {
      AuthState.currentUser.value = userData
      AuthState.currentRole.value = userData.role ?? null
    })
  },
  getMenu: async () => {
    const { data: menu } = await appRequests.Auth.getMenu()

    AuthState.menu.value = menu
  },
  handleActivateAccount: async (accessToken: string) => {
    const authData = await appRequests.Auth.activateAccount(accessToken)

    const token = authData.data.token.bearer

    authStateActions.handleSetAuthToken(token)
  },
  handleChangeCurrentRole: async (roleId?: number) => {
    const newRoleId = roleId ?? AuthState.currentRole.peek()

    const {
      data: { changed, roles },
    } = await appRequests.Auth.changeCurrentRole(Number(newRoleId))

    if (!changed) throw Error('Error changing current role')

    AuthState.currentRole.value = AuthState.currentRole.peek()
    AuthState.roles.value = roles
  },
  handleCheckResetPwdToken: async (accessToken: string) => {
    const { data: validateData } =
      await appRequests.Auth.authValidateForgotPwd(accessToken)

    if (!validateData.validated) throw Error('Error validating token')

    AuthState.resetPwdCode.value = validateData.resetPwdCode
  },
  handleCloseAuthModal: () => {
    AuthState.authModal.value = {
      data: null,
      open: false,
      type: null,
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleForgotPwd: async (forgotPwdData: TForgotPwdData) => {
    await appRequests.Auth.authForgotPwd(forgotPwdData.email)
  },
  handleLogin: async (signinData: TSignIn) => {
    if (OfflineState.isOnline.peek()) {
      const altchaValue = signinData.altcha ?? AltchaState.altcha.peek()

      if (altchaValue) {
        signinData.altcha = altchaValue
      }

      const refreshData = encryptData(JSON.stringify(signinData))

      const authData = await appRequests.Auth.authLogin(refreshData)

      const token = authData.data.token
      authStateActions.handleSetAuthToken(token)

      const userData = authData.data.user

      authStateActions.setUserData(userData)

      const userId = authData.data.user.id

      if (userId) {
        sessionStorage.setItem(offlineDB.userStoreIds.user, encryptData(userId))

        await sessionsDB.saveToStore({
          data: encryptData(JSON.stringify(signinData)),
          id: reverseStringValue(userId),
        })
      }

      await Promise.all([
        applicationsStateActions.getAllApplicationsOffline(),
        authStateActions.handleSaveRefreshData(refreshData),
        authStateActions.handleSaveUserDataToLocalDB(),
      ])

      await initSyncAfterLogin()
      await authStateActions.getInitialData()
    } else {
      await authStateActions.handleLoginOffline(signinData)
    }
  },
  handleLoginOffline: async (signinData: TSignIn) => {
    const userId = await getOfflineUserId(signinData)

    if (!userId) {
      throw new Error('Usuario no encontrado')
    }

    AuthState.currentUser.value = {
      id: userId,
    }

    await authStateActions.setOfflineLoginData()
  },
  handleLogout: async () => {
    const userIdSession = OfflineState.DBName.peek()

    if (userIdSession) {
      await sessionsDB.deleteFromStore({ key: userIdSession })
    }

    batch(() => {
      cleanDBOnLogOut()

      AuthState.currentUser.value = {}
      AuthState.token.value = null
      AuthState.menu.value = []
      AuthState.permissions.value = []
      AuthState.currentRole.value = null
      ConvocatoriasState.currentConvocatoria.value = {}
      ConvocatoriasState.convocatorias.value = []
      ConvocatoriasState.currentCity.value = null
      ApplicationsState.applications.value = []
      ApplicationsState.currentEntityTableData.value = []
      ApplicationsState.currentApplication.value = null
      ApplicationsState.filters.value = {
        status: null,
        text: null,
        type: null,
      }
      formStateActions.cleanFormState()

      sessionStorage.clear()
    })
  },
  handleRefreshToken: async (signinData: string) => {
    const authData = await appRequests.Auth.authLogin(signinData)

    const token = authData.data.token

    authStateActions.handleSetAuthToken(token)
    AuthState.currentUser.value = authData.data.user
    AuthState.currentRole.value = authData.data?.user?.role ?? null
    authStateActions.setUserPermissions(authData.data?.user?.permission ?? [])

    const userId = authData.data.user.id

    if (userId) {
      sessionStorage.setItem(offlineDB.userStoreIds.user, encryptData(userId))
    }

    await Promise.all([
      authStateActions.handleSaveUserDataToLocalDB(),
      convocatoriasStateActions.getUserConvocatorias(),
    ])
  },
  handleResetPwd: async (resetPwdData: TResetPwdData) => {
    const authData = await appRequests.Auth.authResetPwd({
      code: AuthState.resetPwdCode.peek(),
      password: resetPwdData.password,
    })

    const token = authData.data.token.bearer

    authStateActions.handleSetAuthToken(token)
  },
  handleSaveRefreshData: async (signinData: string) => {
    sessionStorage.setItem(offlineDB.userStoreIds.refreshData, signinData)
    await offlineDB.saveMultipleToStore(offlineDB.stores.USER_STORE, [
      {
        data: signinData,
        id: offlineDB.userStoreIds.refreshData,
      },
    ])
  },
  handleSaveUserDataToLocalDB: async () => {
    const userData = AuthState.currentUser.peek()
    let userDataEncrypted = null

    if (userData) {
      userDataEncrypted = encryptData(JSON.stringify(userData))
    }

    const token = AuthState.token.peek()
    let tokenEncrypted = null

    if (token) {
      tokenEncrypted = encryptData(token)
    }

    await offlineDB.saveMultipleToStore(offlineDB.stores.USER_STORE, [
      {
        data: userDataEncrypted,
        id: offlineDB.userStoreIds.user,
      },
      {
        data: tokenEncrypted,
        id: offlineDB.userStoreIds.token,
      },
    ])
  },
  handleSetAuthModalType: (type: EAuthModalTypes) => {
    batch(() => {
      let authData = null

      if (type === EAuthModalTypes.AUTH_PERSONAL_DATA) {
        authData = AUTH_MODALS_INFO.PersonalData
      }

      if (type === EAuthModalTypes.TERMS_AND_CONDITIONS) {
        authData = AUTH_MODALS_INFO.TermsAndConditions
      }

      AuthState.authModal.value = {
        data: authData,
        open: true,
        type,
      }
    })
  },
  handleSetAuthToken: (token: string) => {
    AuthState.token.value = token

    sessionStorage.setItem('token', encryptData(token))
  },
  handleSignup: async (signupData: TSignupData) => {
    const { confirmEmail, confirmPassword, ...REST_SIGNUP_DATA } = signupData

    const { data: response } =
      await appRequests.Auth.authSignup(REST_SIGNUP_DATA)

    if (!response.created) throw Error('Error registering new user')
  },
  reloadCurrentRole: async () => {
    await Promise.all([authStateActions.getMe(), authStateActions.getMenu()])
  },
  setOfflineLoginData: async () => {
    const { convocatorias, formEntities, token, userData } =
      await getOfflineDataByUser()

    authStateActions.handleSetAuthToken(token)
    authStateActions.setUserData(userData)
    await convocatoriasStateActions.setConvocatorias(convocatorias)
    await formStateActions.setCurrentForm(formEntities)
    await applicationsStateActions.getAllApplicationsOffline()
  },

  setUserData: (userData: TAuthCurrentUserState) => {
    AuthState.currentUser.value = userData
    AuthState.currentRole.value = userData.role ?? null
    authStateActions.setUserPermissions(userData.permission ?? [])

    const userId = userData.id

    if (userId) {
      sessionStorage.setItem(offlineDB.userStoreIds.user, encryptData(userId))
    }
  },
  setUserPermissions: (permissions: TAuthUserPermissionResponse[]) => {
    const userPermissions = setUserPermissions(permissions)

    AuthState.userPermissions.value = { ...userPermissions }

    setPermisionsToForm()
  },
}

effect(() => {
  const userRefresh = AuthState.userRefresh.value

  if (userRefresh) {
    const userRefreshDecrypted = JSON.parse(decryptData(userRefresh)) as TSignIn

    authStateActions.handleLogin(userRefreshDecrypted).then(async () => {
      const applicationData = await offlineDB.getFromStore(
        offlineDB.stores.USER_STORE,
        offlineDB.userStoreIds.application
      )

      if (applicationData) {
        const applicationId = decryptData(applicationData.data as string)

        const applicationExist = ApplicationsState.applications
          .peek()
          .find((item) => item.id === applicationId)

        if (applicationExist) {
          await applicationsStateActions.viewApplication(applicationId)
        } else {
          await offlineDB.deleteFromStore({
            key: offlineDB.userStoreIds.application,
            storeName: offlineDB.stores.USER_STORE,
          })
        }
      }
    })
  }
})
